import { useMemo } from 'react';
import { useMutation } from 'react-query';
import { InvoiceDetailModel, PaymentOrigin } from '@frontend/api-invoices';
import { PaymentMethod } from '@frontend/api-payment-plans';
import { SetupFutureUsage } from '@frontend/api-weave-pay';
import { PaymentsFeatureFlags, useMemoizedArray } from '@frontend/payments-hooks';
import { useAppFlagStore } from '@frontend/shared';
import {
  useConfirmStripePaymentViaPaymentMethod,
  UseCreatePaymentIntentSyncOptions,
  useInvoicePerson,
  useMakeStripePayment,
} from '../../../../../hooks';

interface UseCardOnFilePaymentOptions {
  paymentMethod?: PaymentMethod;
  invoice?: InvoiceDetailModel;
  onInvoiceAlreadyPaid: UseCreatePaymentIntentSyncOptions['onInvoiceAlreadyPaid'];
  onRefetchInvoice: UseCreatePaymentIntentSyncOptions['onRefetchInvoice'];
}

export const useCardOnFilePayment = ({
  paymentMethod,
  invoice,
  onInvoiceAlreadyPaid,
  onRefetchInvoice,
}: UseCardOnFilePaymentOptions) => {
  const locationId = invoice?.locationId;
  const personId = invoice?.person.id;
  const { personEmail } = useInvoicePerson(invoice);
  const email = personEmail;
  const amount = invoice?.billedAmount ?? 0;

  const { makePayment: makePaymentEnabled } = useConfirmStripePaymentViaPaymentMethod({
    amount,
    email,
    personId,
    locationId,
    origin: PaymentOrigin.ManualCardEntry,
    /**
     * Stripe defaults to onSession if unspecified. Even if the card is already on file,
     * and most cards were saved with onSession so OTP is being triggered.
     * To prevent OTP for card on file we are now passing offSession with the Backend updates
     *
     * If we need use this hook elsewhere, we can use a prop to decide this.
     */
    setupFutureUsage: SetupFutureUsage.offSession,
    paymentMethod,
    invoiceId: invoice?.id,
    onInvoiceAlreadyPaid,
    onRefetchInvoice,
  });

  /**
   * TODO: Temp fallback code
   * Remove once FF PaymentsFeatureFlags.EnableSurcharging is rolled out
   */

  const { getFeatureFlagValue } = useAppFlagStore();

  const locationIds = useMemoizedArray([locationId]);

  const enableSurcharging = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.EnableSurcharging,
    locationIds: locationIds,
    strategy: 'ALL',
  });

  const { makePayment: makePaymentDisabled } = useMakeStripePayment({
    amount,
    email,
    personId,
    locationId,
    origin: PaymentOrigin.ManualCardEntry,
    /**
     * Stripe defaults to onSession if unspecified. Even if the card is already on file,
     * and most cards were saved with onSession so OTP is being triggered.
     * To prevent OTP for card on file we are now passing offSession with the Backend updates
     *
     * If we need use this hook elsewhere, we can use a prop to decide this.
     */
    setupFutureUsage: SetupFutureUsage.offSession,
    paymentMethod,
    invoiceId: invoice?.id,
    onInvoiceAlreadyPaid,
    onRefetchInvoice,
  });

  const makePayment = useMemo(() => {
    if (enableSurcharging) {
      return makePaymentEnabled;
    }
    return makePaymentDisabled;
  }, [enableSurcharging, makePaymentEnabled, makePaymentDisabled]);

  const { mutateAsync: makeCardOnFilePayment, isLoading: makingCardOnFilePayment } = useMutation({
    mutationFn: () => makePayment(),
  });

  return { makeCardOnFilePayment, makingCardOnFilePayment };
};
